import React, { useState, useEffect, useRef, useImperativeHandle } from "react";
import { Plant, GardenObject } from "../types";
import { apiClient } from "../../utils/apiClient/apiClient";

interface ItemSelectorProps {
  onItemSelected: (
    item: Plant | GardenObject | null,
    type: "plant" | "object"
  ) => void;
  selectedItem?: Plant | GardenObject | null;
  selectedType?: "plant" | "object";
  company_id: string;
  disabled?: boolean;
}

export interface ItemSelectorRef {
  getItems: () => ItemOption[];
  getItemTypes: () => ("plant" | "object")[];
  getAllItems: () => { plant: ItemOption[]; object: ItemOption[] };
  getRandomItem: () => {
    item: Plant | GardenObject;
    type: "plant" | "object";
  } | null;
}

interface ItemOption {
  id: string;
  name: string;
  type: "plant" | "object";
  item: Plant | GardenObject;
}

const ItemSelector = React.forwardRef<ItemSelectorRef, ItemSelectorProps>(
  (
    {
      onItemSelected,
      selectedItem,
      selectedType,
      company_id,
      disabled = false,
    },
    ref
  ) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedItemType, setSelectedItemType] = useState<
      "plant" | "object"
    >(selectedType || "plant");
    console.log("DATA: ", selectedType, selectedItemType, selectedItem);
    const [items, setItems] = useState<ItemOption[]>([]);
    const [allItems, setAllItems] = useState<{
      plant: ItemOption[];
      object: ItemOption[];
    }>({
      plant: [],
      object: [],
    });
    const [filteredItems, setFilteredItems] = useState<ItemOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getItems: () => items,
      getItemTypes: () => ["plant", "object"],
      getAllItems: () => allItems,
      getRandomItem: () => {
        const types = Object.keys(allItems) as ("plant" | "object")[];
        const availableTypes = types.filter(
          (type) => allItems[type].length > 0
        );

        if (availableTypes.length === 0) return null;

        const randomType =
          availableTypes[Math.floor(Math.random() * availableTypes.length)];
        const typeItems = allItems[randomType];

        if (typeItems.length === 0) return null;

        const randomItem =
          typeItems[Math.floor(Math.random() * typeItems.length)];
        return {
          item: randomItem.item,
          type: randomType,
        };
      },
    }));

    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Sync selectedItemType with selectedType prop
    useEffect(() => {
      if (selectedType && selectedType !== selectedItemType) {
        setSelectedItemType(selectedType);
      }
    }, [selectedType]);

    // Load items when component mounts or type changes
    useEffect(() => {
      loadItems();
    }, [selectedItemType]);

    // Load both plant and object data on mount for random selection
    useEffect(() => {
      const loadAllData = async () => {
        setIsLoading(true);
        try {
          // Load plants
          const available_items = await apiClient.listAvailableInventory(
            company_id
          );
          console.log("PLANTS: ", available_items);
          const plantItems = available_items.plants.map((plant: Plant) => ({
            id: plant.plant_id || "",
            name: plant.common_name || "Unnamed Plant",
            type: "plant" as const,
            item: plant,
          }));

          // Load objects
          const objectItems = available_items.objects.map(
            (obj: GardenObject) => ({
              id: obj.object_id,
              name: obj.name || "Unnamed Object",
              type: "object" as const,
              item: obj,
            })
          );

          setAllItems({
            plant: plantItems,
            object: objectItems,
          });
          setItems(plantItems);
        } catch (error) {
          console.error("Error loading all items:", error);
        } finally {
          setIsLoading(false);
        }
      };

      loadAllData();
    }, []); // Only run on mount

    // Filter items based on search term
    useEffect(() => {
      if (searchTerm.trim() === "") {
        setFilteredItems(items.slice(0, 10)); // Show first 10 items when no search
      } else {
        const filtered = items
          .filter((item) =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .slice(0, 20); // Limit to 20 results for performance
        setFilteredItems(filtered);
      }
      setHighlightedIndex(-1);
    }, [searchTerm, items]);

    // Handle clicking outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsDropdownOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const loadItems = async () => {
      setIsLoading(true);
      try {
        // Always use API calls to get full plant/object data
        // Inventory data doesn't contain enriched plant_data/object_data
        await loadFromAPI();
      } catch (error) {
        console.error(`Error loading ${selectedItemType}s:`, error);
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    const loadFromAPI = async () => {
      try {
        let fetchedItems: ItemOption[] = [];

        if (selectedItemType === "plant") {
          fetchedItems = allItems.plant;
        } else {
          fetchedItems = allItems.object;
        }

        setItems(fetchedItems);

        // Also store in allItems for cross-type access
        setAllItems((prev) => ({
          ...prev,
          [selectedItemType]: fetchedItems,
        }));
      } catch (error) {
        console.error(`Error loading ${selectedItemType}s from API:`, error);
        setItems([]);
      }
    };

    const handleItemTypeChange = (type: "plant" | "object") => {
      setSelectedItemType(type);
      setSearchTerm("");
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
      onItemSelected(null, type);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      setIsDropdownOpen(true);
    };

    const handleItemSelect = (itemOption: ItemOption) => {
      setSearchTerm(itemOption.name);
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
      onItemSelected(itemOption.item, itemOption.type);
    };

    const handleClearSelection = () => {
      setSearchTerm("");
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
      onItemSelected(null, selectedItemType);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!isDropdownOpen) {
        if (e.key === "ArrowDown" || e.key === "Enter") {
          setIsDropdownOpen(true);
          setHighlightedIndex(0);
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredItems.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
          e.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredItems.length
          ) {
            handleItemSelect(filteredItems[highlightedIndex]);
          }
          break;
        case "Escape":
          setIsDropdownOpen(false);
          setHighlightedIndex(-1);
          break;
      }
    };

    const displayValue = selectedItem
      ? (() => {
          // Error checking: verify the selectedItem matches the selectedItemType
          if (selectedItemType === "plant") {
            // Check if this is actually a plant by looking for plant-specific properties
            if (
              "plant_id" in selectedItem ||
              "common_name" in selectedItem ||
              "scientific_name" in selectedItem
            ) {
              return (selectedItem as Plant).common_name;
            } else {
              // Mismatch detected - return empty string to avoid error
              console.warn(
                'Type mismatch: selectedItemType is "plant" but selectedItem appears to be an object'
              );
              return "";
            }
          } else {
            // Check if this is actually an object by looking for object-specific properties
            if ("object_id" in selectedItem || "category" in selectedItem) {
              return (selectedItem as GardenObject).name;
            } else {
              // Mismatch detected - return empty string to avoid error
              console.warn(
                'Type mismatch: selectedItemType is "object" but selectedItem appears to be a plant'
              );
              return "";
            }
          }
        })()
      : searchTerm;

    return (
      <div className="item-selector" ref={dropdownRef}>
        {/* Type Selector */}
        <div className="form-group mb-3">
          <label className="form-label">Item Type</label>
          <div className="btn-group w-100" role="group">
            <button
              type="button"
              className={`btn ${
                selectedItemType === "plant"
                  ? "btn-primary"
                  : "btn-outline-primary"
              }`}
              onClick={() => handleItemTypeChange("plant")}
              disabled={disabled}
            >
              Plant
            </button>
            <button
              type="button"
              className={`btn ${
                selectedItemType === "object"
                  ? "btn-primary"
                  : "btn-outline-primary"
              }`}
              onClick={() => handleItemTypeChange("object")}
              disabled={disabled}
            >
              Object
            </button>
          </div>
        </div>

        {/* Search Input */}
        <div className="form-group">
          <label className="form-label">
            Search {selectedItemType === "plant" ? "Plants" : "Objects"}
          </label>
          <div className="input-group">
            <input
              ref={inputRef}
              type="text"
              className="form-control"
              placeholder={`Search for ${
                selectedItemType === "plant" ? "plants" : "objects"
              }...`}
              value={displayValue}
              onChange={handleSearchChange}
              onFocus={() => setIsDropdownOpen(true)}
              onKeyDown={handleKeyDown}
              disabled={
                disabled ||
                (selectedItem !== undefined && selectedItem !== null)
              }
            />
            {selectedItem && (
              <button
                type="button"
                className="btn btn-outline-danger btn-outline-close"
                onClick={handleClearSelection}
                disabled={disabled}
                title="Clear selection"
              >
                ×
              </button>
            )}
            {/* <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              disabled={disabled}
            >
              <i className="bi bi-chevron-down"></i>
            </button> */}
          </div>
        </div>

        {/* Dropdown Results */}
        {isDropdownOpen && (
          <div
            className="dropdown-menu show w-100"
            style={{ maxHeight: "300px", overflowY: "auto" }}
          >
            {isLoading ? (
              <div className="dropdown-item text-center">
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                ></div>
                Loading {selectedItemType === "plant" ? "plants" : "objects"}...
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="dropdown-item text-muted">
                {searchTerm.trim() === ""
                  ? `No ${
                      selectedItemType === "plant" ? "plants" : "objects"
                    } found`
                  : `No ${
                      selectedItemType === "plant" ? "plants" : "objects"
                    } match "${searchTerm}"`}
              </div>
            ) : (
              filteredItems.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  className={`dropdown-item ${
                    index === highlightedIndex ? "active" : ""
                  }`}
                  onClick={() => handleItemSelect(item)}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{item.name}</span>
                    <small className="text-muted">
                      {selectedItemType === "plant"
                        ? (item.item as Plant).scientific_name
                        : (item.item as GardenObject).category}
                    </small>
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {/* Selected Item Display */}
        {selectedItem && (
          <div className="mt-2 p-2 border rounded bg-light">
            <small className="text-muted">Selected:</small>
            <div className="fw-bold">
              {(() => {
                // Error checking: verify the selectedItem matches the selectedItemType
                if (selectedItemType === "plant") {
                  if (
                    "plant_id" in selectedItem ||
                    "common_name" in selectedItem ||
                    "scientific_name" in selectedItem
                  ) {
                    return (selectedItem as Plant).common_name;
                  } else {
                    console.warn(
                      'Type mismatch: selectedItemType is "plant" but selectedItem appears to be an object'
                    );
                    return "Unknown Plant";
                  }
                } else {
                  if (
                    "object_id" in selectedItem ||
                    "category" in selectedItem
                  ) {
                    return (selectedItem as GardenObject).name;
                  } else {
                    console.warn(
                      'Type mismatch: selectedItemType is "object" but selectedItem appears to be a plant'
                    );
                    return "Unknown Object";
                  }
                }
              })()}
            </div>
            {selectedItemType === "plant" &&
              "scientific_name" in selectedItem &&
              (selectedItem as Plant).scientific_name && (
                <div className="text-muted small">
                  {(selectedItem as Plant).scientific_name}
                </div>
              )}
            {selectedItemType === "object" &&
              "category" in selectedItem &&
              (selectedItem as GardenObject).category && (
                <div className="text-muted small">
                  Category: {(selectedItem as GardenObject).category}
                </div>
              )}
          </div>
        )}
      </div>
    );
  }
);

ItemSelector.displayName = "ItemSelector";

export default ItemSelector;
